import { useState, useEffect, useContext } from "react";
import { MdAdd } from "react-icons/md";
import { useTaskMaster } from "./Hooks/useTaskMaster";
import { FilterCard, Pagination } from "../../Components/Common";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { CATEGORY_TYPE, PROJECT_TYPE } from "./constants";
import { CREATE_TASK_MASTER } from "../../routes";
import { TaskMasterTable } from "./Components";
import { UserContext } from "../../Context/UserContext";
import { ROLES } from "../../Constants/Strings";

const TaskMasterList = () => {
  const {
    loading,
    error,
    filters,
    setFilters,
    taskMasters,
    setTaskMasters,
    fetchTaskMasters,
    editTaskMaster,
    deleteTaskMasterById,
    fetchSubTasks,
    editSubTask,
    deleteSubTaskById,
  } = useTaskMaster();

  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [isDescModalOpen, setIsDescModalOpen] = useState(false);
  const [selectedDescTask, setSelectedDescTask] = useState(null);

  const [searchText, setSearchText] = useState(filters.search || "");

  const [currentTasks, setCurrentTasks] = useState(taskMasters);
  const [taskStack, setTaskStack] = useState([]); // breadcrumb stack

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(currentTasks.length / rowsPerPage);
  const paginatedTasks = currentTasks.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleViewTask = (task) => {
    setSelectedDescTask(task);
    setIsDescModalOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };

  const handleSaveTask = async () => {
    if (!selectedTask) return;

    // if sub-task has a super_task field
    if (selectedTask.super_task) {
      const masterId = selectedTask.super_task;
      const updatedSubTasks = await editSubTask(masterId, selectedTask.id, {
        title: selectedTask.title,
        description: selectedTask.description,
        weight: selectedTask.weight,
      });

      if (updatedSubTasks) {
        setCurrentTasks((prev) =>
          prev.map((task) =>
            task.id === updatedSubTasks.id ? updatedSubTasks : task
          )
        ); // updated the visible sub-task list
        setTaskStack((prevStack) =>
          prevStack.map((parent) => ({
            ...parent,
            sub_tasks: parent.sub_tasks?.map((t) =>
              t.id === updatedSubTasks.id ? updatedSubTasks : t
            ),
          }))
        ); // update breadcrumb stack
        setTaskMasters((prevMasters) =>
          prevMasters.map((master) =>
            master.id === masterId
              ? {
                  ...master,
                  sub_tasks: master.sub_tasks?.map((t) =>
                    t.id === updatedSubTasks.id ? updatedSubTasks : t
                  ),
                }
              : master
          )
        ); // update master task in task master
        setIsEditModalOpen(false);
        setSelectedTask(null);
      }
    } else {
      // it's a master task
      const updatedMaster = await editTaskMaster(selectedTask);

      if (updatedMaster) {
        setCurrentTasks((prev) => 
          prev.map((task) =>
            task.id === updatedMaster.id ? updatedMaster : task
          )
        ); 
        // update master task list
        setTaskStack((prev) =>
          prev.map((t) => (t.id === updatedMaster.id ? updatedMaster : t))
        );
        setIsEditModalOpen(false);
        setSelectedTask(null);
      }
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    if (task.super_task) {
      const masterId = task.super_task;
      const success = await deleteSubTaskById(masterId, task.id);

      if (success !== false) {
        setTaskMasters((prevMasters) =>
          prevMasters.map((master) =>
            master.id === masterId
              ? {
                  ...master,
                  sub_tasks: master.sub_tasks?.filter((t) => t.id !== task.id),
                }
              : master
          )
        ); // update sub task count in master list
        setCurrentTasks((prev) => prev.filter((t) => t.id !== task.id)); // remove sub-task from currentTasks
        setTaskStack((prevStack) =>
          prevStack.map((parent) => ({
            ...parent,
            sub_tasks: parent.sub_tasks?.filter((t) => t.id !== task.id),
          }))
        ); // remove from parent task in taskstack
      }
    } else {
      const success = await deleteTaskMasterById(task.id);

      if (success) {
        setCurrentTasks((prev) => prev.filter((t) => t.id !== task.id));
        setTaskStack((prevStack) => prevStack.filter((t) => t.id !== task.id)); // if the deleted master task is in the breadcrumb stack
      }
    }
  };

  const handleDrillDown = async (task) => {
    if (!task.sub_tasks || task.sub_tasks.length === 0) {
      const fetchedSubTasks = await fetchSubTasks(task.id);
      setCurrentTasks(fetchedSubTasks ? fetchedSubTasks : []);
    } else {
      setTaskStack((prev) => [...prev, task]);
      setCurrentTasks(task.sub_tasks);
    }
  };

  useEffect(() => {
    fetchTaskMasters();
  }, [filters]);

  // Update current tasks when main taskMasters change
  useEffect(() => {
    if (taskStack.length === 0) setCurrentTasks(taskMasters);
  }, [taskMasters, taskStack]);

  useEffect(() => {
    setPage(1);
  }, [currentTasks, rowsPerPage]);

  return (
    <div className="py-2 px-3">
      <div className="flex items-center justify-between md:mb-4 my-4 gap-8">
        <div className="flex justify-between gap-3 my-2 md:my-0">
          <button
            onClick={() => navigate(-1)}
            className="text-primary text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={25} className="text-primary" />
          </button>
          <h1 className="text-2xl font-bold">Task Masters</h1>
        </div>

        <button
          onClick={() => navigate(CREATE_TASK_MASTER)}
          disabled={
            ![
              ROLES.MANAGING_DIRECTOR,
              ROLES.HEAD_OFFICE,
              ROLES.GOVERNMENT_OFFICER,
            ].includes(user?.role)
          }
          className={`px-3 py-2 rounded-md flex items-center gap-2 ${
            [
              ROLES.MANAGING_DIRECTOR,
              ROLES.HEAD_OFFICE,
              ROLES.GOVERNMENT_OFFICER,
            ].includes(user?.role)
              ? "bg-primary text-white hover:bg-blue-700"
              : "bg-primary text-white cursor-not-allowed"
          }`}
          aria-label="Add Task"
        >
          <MdAdd size={18} />
          <span className="whitespace-nowrap">Add Task</span>
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="flex flex-col md:flex-row md:justify-between md:items-end mb-4 gap-6 md:gap-0">
        <div className="order-1 md:order-2">
          <FilterCard
            tempFilters={filters}
            setTempFilters={setFilters}
            searchText={searchText}
            setSearchText={setSearchText}
            fields={[
              {
                key: "search",
                type: "search",
                placeholder: "Search by Task Title",
              },
              {
                key: "project_type",
                label: "Type",
                type: "select",
                options: [
                  { value: "", label: "All" },
                  ...Object.entries(PROJECT_TYPE).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ],
              },
              {
                key: "project_category",
                label: "Category",
                type: "select",
                options: [
                  { value: "", label: "All" },
                  ...Object.entries(CATEGORY_TYPE).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ],
              },
            ]}
          />
        </div>
        <div className="pl-2 text-sm font-semibold italic text-gray-600 dark:text-gray-300 flex gap-1 items-center order-2 md:order-1">
          <span
            className="cursor-pointer text-primary hover:text-blue-800"
            onClick={() => {
              setTaskStack([]);
              setCurrentTasks(taskMasters);
            }}
          >
            Main Tasks
          </span>

          {taskStack.map((task, index) => (
            <span key={task.id} className="flex items-center gap-1">
              <span className="font-bold">{">"}</span>
              <span
                className="cursor-pointer text-primary hover:text-blue-800"
                onClick={() => {
                  const newStack = taskStack.slice(0, index + 1);
                  setTaskStack(newStack);
                  setCurrentTasks(task.sub_tasks || []);
                }}
              >
                {task.title}
              </span>
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center w-full h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
        </div>
      ) : taskMasters?.length === 0 ? (
        <div className="flex justify-center items-center w-full h-40 text-gray-500 text-lg font-medium">
          No Tasks added
        </div>
      ) : (
        <>
          <TaskMasterTable
            taskMasters={paginatedTasks}
            onView={handleViewTask}
            onEdit={handleEditTask}
            onDelete={handleDelete}
            fetchSubTasks={fetchSubTasks}
            onDrillDown={handleDrillDown}
            isSubTaskView={taskStack.length > 0}
          />

          {!loading && currentTasks.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setRowsPerPage(size);
                setPage(1);
              }}
            />
          )}
        </>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-3 w-full max-w-3xl relative">
            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Edit Task</h2>
              <p className="text-gray-500 text-sm mt-1">
                Update task details below
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={selectedTask.title}
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        title: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={selectedTask.description || ""}
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        description: e.target.value,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows={3}
                    placeholder="Enter task description"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Project Type
                  </label>
                  <select
                    value={selectedTask.project_type}
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        project_type: e.target.value,
                      })
                    }
                    className="border bg-gray-100 rounded-lg p-2 w-full cursor-not-allowed"
                    disabled
                  >
                    {Object.entries(PROJECT_TYPE).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Project Category
                  </label>
                  <select
                    value={selectedTask.project_category}
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        project_category: e.target.value,
                      })
                    }
                    className="border bg-gray-100 rounded-lg p-2 w-full cursor-not-allowed"
                    disabled
                  >
                    {Object.entries(CATEGORY_TYPE).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Weight
                  </label>
                  <input
                    type="number"
                    value={
                      selectedTask.weightInput ?? selectedTask.weight ?? ""
                    }
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        weightInput: e.target.value.replace(/^0+/, ""),
                      })
                    }
                    onBlur={() =>
                      setSelectedTask({
                        ...selectedTask,
                        weight: Number(
                          selectedTask.weightInput || selectedTask.weight || 0
                        ),
                        weightInput: undefined,
                      })
                    }
                    className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    min={1}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="bg-red-600 hover:bg-red-800 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTask}
                className="bg-primary hover:bg-blue-900 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {isDescModalOpen && selectedDescTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-xl p-3 w-full max-w-3xl relative">
            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">{selectedDescTask.title}</h2>
              <p className="text-gray-500 text-sm mt-1">Task Details</p>
            </div>

            <div className="px-6 py-4 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-1">Description</h3>
                <p className="text-gray-700">
                  {selectedDescTask.description || "No description available."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-medium text-gray-600">
                    Project Type
                  </h4>
                  <p className="text-gray-800">
                    {PROJECT_TYPE[selectedDescTask.project_type]}
                  </p>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-600">
                    Project Category
                  </h4>
                  <p className="text-gray-800">
                    {CATEGORY_TYPE[selectedDescTask.project_category]}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-medium text-gray-600">Weight</h4>
                  <p className="text-gray-800">{selectedDescTask.weight}</p>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-600">
                    Sub Tasks
                  </h4>
                  <p className="text-gray-800">
                    {selectedDescTask.sub_tasks?.length || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setIsDescModalOpen(false)}
                className="bg-primary hover:bg-blue-800 text-white px-5 py-2 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskMasterList;
